import React, { useState, useContext, useEffect } from "react";
import { v4 } from "uuid";
import moment from "moment";
import { CircularProgress, Grid, Typography } from "@material-ui/core";
import WrappedSidebarV2 from "v2/ui/components/SidebarV2";
import { useParams, Link } from "react-router-dom";
import { useTreatments } from "@splitsoftware/splitio-react";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import SectionContentWrapper from "../../../../components/SectionContentWrapper";
import DataRow from "./components/DataRow";
import InfiniteTable from "../../../../components/InfiniteTable";
import RecurrentPaymentContext from "../../../../../infrastructure/Contexts/components/data/RecurrentPaymentsWrapper/context";
import { recurrentPaymentDetailsConfig } from "./config";
import { ReactComponent as FileIcon } from "../../../../../assets/icons/file.svg";
import { ReactComponent as BackIcon } from "../../../../../assets/icons/back.svg";
import Approvers from "./components/Approvers";
import StateLabel from "../../../../components/StateLabel";
import useCommonStyles from "../../../../../infrastructure/styles";
import useStyles from "./styles";
import CobeeButton from "../../../../components/CobeeButton";
import ModalContext from "../../../../../infrastructure/Contexts/components/ModalWrapper/context";
import CompanyContext from "../../../../../infrastructure/Contexts/components/data/CompanyWrapper/context";
import { configStates, parseStateColor } from "../../config";
import RentingFiles from "./components/RentingFiles";
import Beneficiaries from "./components/Beneficiaries";
import { translate as t } from "../../../../../infrastructure/translate/localization";

interface Params {
  type: string;
  id: string;
}

const today = moment(new Date()).format("YYYY-MM-DD");
const past = moment(new Date()).subtract(1, "y").format("YYYY-MM-DD");
const INITIAL_FILTERS_QUERY = `minRequestedAt=${past}&maxRequestedAt=${today}&pageSize=10`;

export default function RecurrentPaymentDetailScreen(): JSX.Element {
  const { type, id: recurrentPaymentId } = useParams<Params>();

  const { sidebarSpacer, container } = useCommonStyles();
  const {
    titleContainer,
    loaderContainer,
    title,
    subTitle,
    dateLabel,
    headerSeparator,
    infoContainer,
    infoLabel,
    dateRangeContainer,
    symbolsContainer,
    greyPoint,
    verticalLine,
    dateRowSeparator,
    dateContainer,
    dateValue,
    buttonsContainer,
    containerReason,
    reason,
    whyQuestion,
    textRentingReason,
    buttonRentingReason,
    tableTitle,
    backLabel,
  } = useStyles();

  const { company } = useContext(CompanyContext);
  const { id: companyId } = company;
  const {
    recurrentPaymentDetail,
    setRecurrentPaymentDetail,
    loadRecurrentPaymentDetail,
    isLoading,
    setRentingInitialData,
  } = useContext(RecurrentPaymentContext);
  const { openModal, closeModal } = useContext(ModalContext);

  const [detailData, setDetailData] = useState<any>(null);
  const [isOpenRentingFiles, setIsOpenRentingFiles] = useState<boolean>(false);

  useEffect(() => {
    if (recurrentPaymentDetail === null && !isLoading) {
      loadRecurrentPaymentDetail({ companyId, recurrentPaymentId });
    }
  }, [
    loadRecurrentPaymentDetail,
    companyId,
    recurrentPaymentId,
    recurrentPaymentDetail,
    isLoading,
  ]);

  useEffect(() => {
    if (recurrentPaymentDetail !== null) {
      setDetailData(
        recurrentPaymentDetailsConfig(recurrentPaymentDetail)[type]
      );
    }
  }, [recurrentPaymentDetail, type]);

  useEffect(() => {
    return () => {
      setRecurrentPaymentDetail(null);
      setRentingInitialData(null);
    };
  }, [setRentingInitialData, setRecurrentPaymentDetail]);

  const treatments = useTreatments([featureFlagsDictionary.BI_3777], {
    companyId: company?.id ?? "",
    countryISOCode: company?.countryIsoCode ?? "",
  });
  const isTrainingRequestFFEnabled =
    treatments[featureFlagsDictionary.BI_3777].treatment === "on";

  const shouldShowApprovers =
    detailData?.hasApprovers &&
    recurrentPaymentDetail?.approvals?.length > 0 &&
    (recurrentPaymentDetail?.state === "confirm" ||
      recurrentPaymentDetail?.state === "pending");

  const shoulShowActionButtons =
    detailData?.hasActions && recurrentPaymentDetail?.state === "pending";

  const shouldShowReasonBlock =
    detailData?.hasReasonBlock && recurrentPaymentDetail?.state === "rejected";

  const shouldShowBeneficiaries =
    detailData?.hasBeneficiaries &&
    recurrentPaymentDetail?.beneficiaries?.length > 0;

  const shouldShowWhyQuestion =
    detailData?.hasWhyQuestion && recurrentPaymentDetail?.state === "rejected";

  return (
    <Grid container className={sidebarSpacer}>
      <WrappedSidebarV2 />
      <Grid item container xs={12} className={container}>
        <Link
          to={
            type === "education" && isTrainingRequestFFEnabled
              ? `/v2/employee-requests?companyId=${companyId}`
              : `/benefits/recurrent-payments/${type}?${INITIAL_FILTERS_QUERY}`
          }
        >
          <Grid container alignItems="center">
            <BackIcon />
            <Typography variant="body2" className={backLabel}>
              {t("recurrent-payments-section.payment-planning")}
            </Typography>
          </Grid>
        </Link>
        {detailData !== null && !isLoading ? (
          <SectionContentWrapper>
            <Grid container item>
              <Grid item>
                <detailData.Icon />
              </Grid>
              <Grid item className={titleContainer}>
                <Typography className={title}>
                  {detailData.titleText}
                </Typography>
                {detailData.subTitleText && (
                  <Typography variant="body2" className={subTitle}>
                    {detailData.subTitleText}
                  </Typography>
                )}
                <Grid container alignItems="center">
                  <StateLabel
                    text={
                      type in configStates
                        ? configStates[type][recurrentPaymentDetail?.state]
                        : configStates.default[recurrentPaymentDetail?.state]
                    }
                    color={parseStateColor({
                      state: recurrentPaymentDetail?.state,
                      isInsurance:
                        type === "health-insurance" ||
                        type === "life-insurance",
                    })}
                  />
                  {shouldShowWhyQuestion && (
                    <Typography
                      className={whyQuestion}
                      onClick={() =>
                        openModal({
                          title: t(
                            "recurrent-payments-section.renting-has-been-rejected"
                          ),
                          modalContents: (
                            <Grid>
                              <Typography
                                variant="body2"
                                className={textRentingReason}
                              >
                                {t("recurrent-payments-section.the-reason-is")}
                              </Typography>
                              <Typography variant="body2">{`- ${recurrentPaymentDetail.rejectedReason}`}</Typography>
                              <Grid
                                container
                                justifyContent="flex-end"
                                className={buttonRentingReason}
                              >
                                <CobeeButton onClick={closeModal}>
                                  {t("common-messages.understood")}
                                </CobeeButton>
                              </Grid>
                            </Grid>
                          ),
                        })
                      }
                    >
                      {t("common-messages.why")}
                    </Typography>
                  )}
                </Grid>
                <Typography variant="body2" className={dateLabel}>
                  {`${t(
                    "recurrent-payments-section.requested-on-the-day"
                  )} ${moment(recurrentPaymentDetail?.requestDate).format(
                    "D"
                  )} ${moment(recurrentPaymentDetail?.requestDate).format(
                    "MMM"
                  )} ${moment(recurrentPaymentDetail?.requestDate).format(
                    "YYYY"
                  )}`}
                </Typography>
              </Grid>
              {detailData.hasInfo && (
                <Grid
                  container
                  className={infoContainer}
                  onClick={async () => {
                    const link = document.createElement("a");
                    link.target = "_blank";
                    link.href = detailData.policyUrl;
                    link.click();
                  }}
                >
                  <FileIcon />
                  <Typography className={infoLabel}>
                    {t("recurrent-payments-section.policy-info")}
                  </Typography>
                </Grid>
              )}
              {detailData.hasRentingInfo && (
                <Grid className={infoContainer}>
                  <Grid
                    container
                    onClick={() => setIsOpenRentingFiles((prev) => !prev)}
                  >
                    <FileIcon />
                    <Typography className={infoLabel}>
                      {t("recurrent-payments-section.renting-info")}
                    </Typography>
                  </Grid>

                  {isOpenRentingFiles &&
                    recurrentPaymentDetail.rentingFiles.length > 0 && (
                      <RentingFiles
                        rentingFiles={recurrentPaymentDetail.rentingFiles}
                      />
                    )}
                </Grid>
              )}
              {shouldShowApprovers && (
                <Approvers data={recurrentPaymentDetail.approvals} />
              )}
            </Grid>
            <Grid className={headerSeparator} />
            {detailData.rows.map((props) => (
              <DataRow key={v4()} {...props} />
            ))}
            {detailData.hasDateRange && (
              <Grid
                container
                className={dateRangeContainer}
                alignItems="center"
              >
                <Grid className={symbolsContainer}>
                  <div className={greyPoint} />
                  <div className={verticalLine} />
                  <div className={greyPoint} />
                </Grid>
                <Grid container direction="column" className={dateContainer}>
                  <Grid container>
                    <Typography variant="body2">
                      {t("recurrent-payments-section.start-date")}
                    </Typography>
                    <Typography variant="body2" className={dateValue}>
                      {recurrentPaymentDetail?.initialDate
                        ? `${moment(recurrentPaymentDetail?.initialDate).format(
                            "D"
                          )} ${moment(
                            recurrentPaymentDetail?.initialDate
                          ).format("MMM")} ${moment(
                            recurrentPaymentDetail?.initialDate
                          ).format("YYYY")}`
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid className={dateRowSeparator} />
                  <Grid container>
                    <Typography variant="body2">
                      {t("recurrent-payments-section.ending-date")}
                    </Typography>
                    <Typography variant="body2" className={dateValue}>
                      {recurrentPaymentDetail?.finalDate
                        ? `${moment(recurrentPaymentDetail?.finalDate).format(
                            "D"
                          )} ${moment(recurrentPaymentDetail?.finalDate).format(
                            "MMM"
                          )} ${moment(recurrentPaymentDetail?.finalDate).format(
                            "YYYY"
                          )}`
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {shouldShowBeneficiaries && (
              <Beneficiaries
                beneficiaries={recurrentPaymentDetail?.beneficiaries}
              />
            )}
            {shoulShowActionButtons && (
              <Grid container className={buttonsContainer} spacing={2}>
                <Grid item>
                  <CobeeButton
                    styleType="secondary"
                    onClick={() => detailData.rejectAction(openModal)}
                  >
                    {t("actions.refuse")}
                  </CobeeButton>
                </Grid>
                <Grid item>
                  <CobeeButton
                    onClick={() => detailData.acceptAction(openModal)}
                  >
                    {detailData?.acceptLabel}
                  </CobeeButton>
                </Grid>
              </Grid>
            )}
            {shouldShowReasonBlock && (
              <Grid className={containerReason}>
                <Typography>
                  {t(
                    "recurrent-payments-section.training-course-has-been-rejected"
                  )}
                </Typography>
                <Typography
                  className={reason}
                >{`- ${recurrentPaymentDetail.rejectedReason}`}</Typography>
              </Grid>
            )}
            {recurrentPaymentDetail?.transactions.length > 0 && (
              <>
                <Typography className={tableTitle}>
                  {t("recurrent-payments-section.fees-paid").toUpperCase()}
                </Typography>
                <InfiniteTable
                  columns={detailData.tableColumns}
                  data={detailData.parseData(
                    recurrentPaymentDetail.transactions
                  )}
                  hasPagination={false}
                  hasActions={false}
                />
              </>
            )}
          </SectionContentWrapper>
        ) : (
          <Grid
            item
            container
            xs={12}
            justifyContent="center"
            alignItems="center"
            className={loaderContainer}
          >
            <CircularProgress color="secondary" />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
